import React from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { throttle } from 'lodash-es'
import { intl } from 'lang/intl'

import type { Comment } from '../../../../../redux-types'
import { WebinarSidebarSeparator } from '../../../WebinarSidebarFeature/components'
import QuestionComponent from '../QuestionComponent/QuestionComponent'
import CommentInListDndComponent from '../CommentInListComponent/CommentInListDndComponent'

import './GroupedComments.scss'

type Question = Comment<'question'>
type GroupKey = Question['comment_status']

type Props = {
  group: Record<GroupKey, Question[] | undefined>
  onCommentMoved: (fromIndex: number, toIndex: number) => void
  onCommentAnsweredClick: (comment: Question) => void
  onCommentDismissedClick: (comment: Question) => void
  onCommentShowClick: (comment: Question) => void
  currentlyPresentedCommentId?: number
}

const GroupedComments = ({
  group,
  onCommentMoved,
  onCommentAnsweredClick,
  onCommentDismissedClick,
  onCommentShowClick,
  currentlyPresentedCommentId,
}: Props) => {
  const role = useSelector((state: any) => state.webinar.role) as
    | 'producer'
    | 'presenter'
  const isProducer = role === 'producer'

  // Which sections and the order to render them in.
  const sections: GroupKey[] = ['', 'answered', 'dismissed']

  // Transforming section keys intro translated titles.
  // eslint-disable-next-line consistent-return
  const getSectionTitle = (key: GroupKey) => {
    switch (key) {
      case '':
        return intl.formatMessage({ defaultMessage: 'Questions' })
      case 'answered':
        return intl.formatMessage({ defaultMessage: 'Answered' })
      case 'dismissed':
        return intl.formatMessage({ defaultMessage: 'Dismissed' })
    }
  }

  const handleOnCommentMove = throttle(onCommentMoved, 25)
  const shouldShowSeparator = sections
    .slice(1)
    .reduce((shouldShow, sectionKey) => {
      return shouldShow || !!group[sectionKey]?.length
    }, false)

  const totalQuestions = sections.reduce(
    (acc, key) => acc + (group[key]?.length ?? 0),
    0
  )

  return (
    <div className="grouped-comments">
      {sections.map((key) => {
        // Get the comments for the current section.
        const comments = group[key]

        // Do not display the section if it hasn't got any comments.
        if (comments === undefined || comments.length === 0) {
          return null
        }

        // Are we in the answered or dismissed section?
        const isInAnsweredOrDismissedSection = [
          'answered',
          'dismissed',
        ].includes(key)
        const isDndEnabled = isProducer && !isInAnsweredOrDismissedSection
        const isInactive = isInAnsweredOrDismissedSection

        return (
          <React.Fragment key={key}>
            <div className="comments-enclosure">
              <div className="section-header">{getSectionTitle(key)}</div>
              {comments.map((comment, idx) => {
                const isCurrentlyPresentedComment =
                  currentlyPresentedCommentId === +comment.comment_id
                const isPersistingViewButton = isCurrentlyPresentedComment

                let onAnsweredClick
                let onDismissClick
                let onViewClick

                if (isProducer && !isInAnsweredOrDismissedSection) {
                  onAnsweredClick = () => {
                    if (isCurrentlyPresentedComment) {
                      onCommentShowClick(comment)
                    }
                    onCommentAnsweredClick(comment)
                  }
                  onDismissClick = () => {
                    if (isCurrentlyPresentedComment) {
                      onCommentShowClick(comment)
                    }
                    onCommentDismissedClick(comment)
                  }
                  onViewClick = () => {
                    onCommentShowClick(comment)
                  }
                }

                let by = comment.full_name || comment.name
                if (by !== comment.name) {
                  by += ` ${intl.formatMessage(
                    {
                      defaultMessage: '(shown as {name})',
                    },
                    { name: comment.name }
                  )}`
                }

                return (
                  <CommentInListDndComponent
                    index={idx}
                    onCommentMoved={handleOnCommentMove}
                    key={comment.comment_id}
                    isDndEnabled={isDndEnabled}
                  >
                    <QuestionComponent
                      label={comment.content_text}
                      by={by}
                      isUnread={false}
                      isInactive={isInactive}
                      isPersistingViewButton={isPersistingViewButton}
                      onAnsweredClick={onAnsweredClick}
                      onDismissClick={onDismissClick}
                      onViewClick={onViewClick}
                    />
                  </CommentInListDndComponent>
                )
              })}
            </div>
            {shouldShowSeparator && key === sections[0] && (
              <WebinarSidebarSeparator />
            )}
          </React.Fragment>
        )
      })}
      {totalQuestions === 0 && (
        <div className="empty-questions-placeholder">
          <FormattedMessage defaultMessage="This is where you'll find all the questions from the audience. These questions are not visible to the audience, unless they are presented in a TvCard." />
        </div>
      )}
    </div>
  )
}

export default GroupedComments
